/**
 * Enterprise Chart Configuration
 * Unified visual system for all data visualizations
 */
// Sprinklenet Enterprise Color Palette
export const CHART_COLORS = {
  primary: '#1E90FF', // Sprinklenet Blue
  secondary: '#00B5AD', // Teal
  success: '#2ECC71', // Green
  warning: '#F39C12', // Orange
  danger: '#E74C3C', // Red
  neutral: '#BDC3C7', // Gray
  // Extended palette for multi-series
  series: [
    '#1E90FF', // Sprinklenet Blue
    '#00B5AD', // Teal
    '#8B5CF6', // Purple
    '#10B981', // Emerald
    '#F59E0B', // Amber
    '#EF4444', // Red
  ],
  // Semantic colors
  positive: '#2ECC71',
  negative: '#E74C3C',
  atRisk: '#E74C3C',
  normal: '#2ECC71',
  // UI colors
  background: '#F6F8FA',
  gridLine: '#E2E8F0',
  axis: '#A0AEC0',
  labelPrimary: '#1C1F23',
  labelSecondary: '#6C737D',
}
// Typography scale for charts
export const CHART_TYPOGRAPHY = {
  axisLabel: {
    fontSize: 12,
    fontFamily: 'IBM Plex Sans, sans-serif',
    fill: CHART_COLORS.labelSecondary,
    fontWeight: 400,
  },
  dataLabel: {
    fontSize: 13,
    fontFamily: 'IBM Plex Sans, sans-serif',
    fill: CHART_COLORS.labelPrimary,
    fontWeight: 600,
  },
  title: {
    fontSize: 16,
    fontFamily: 'IBM Plex Sans, sans-serif',
    fill: CHART_COLORS.labelPrimary,
    fontWeight: 500,
  },
  tooltip: {
    fontSize: 13,
    fontFamily: 'IBM Plex Sans, sans-serif',
  },
}
// Grid and axis configuration
export const CHART_GRID = {
  stroke: CHART_COLORS.gridLine,
  strokeOpacity: 0.3,
  strokeWidth: 1,
  strokeDasharray: '3 3',
}
export const CHART_AXIS = {
  stroke: CHART_COLORS.axis,
  strokeWidth: 1,
  tick: CHART_TYPOGRAPHY.axisLabel,
}
// Animation configuration
export const CHART_ANIMATION = {
  duration: 300,
  easing: 'ease-out',
}
// Tooltip configuration
export const CHART_TOOLTIP = {
  contentStyle: {
    backgroundColor: 'white',
    border: '2px solid #E2E8F0',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    fontFamily: 'IBM Plex Sans, sans-serif',
  },
  labelStyle: {
    color: CHART_COLORS.labelPrimary,
    fontWeight: 600,
    fontSize: 13,
    marginBottom: 8,
  },
  itemStyle: {
    color: CHART_COLORS.labelSecondary,
    fontSize: 13,
  },
  cursor: {
    stroke: CHART_COLORS.primary,
    strokeWidth: 1,
    strokeOpacity: 0.3,
  },
}
// Number formatting utilities
export const formatNumber = (value: number, decimals: number = 0): string => {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}
export const formatPercent = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`
}
export const formatCurrency = (value: number, decimals: number = 2): string => {
  return `$${formatNumber(value, decimals)}`
}
export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}
// Custom tooltip formatter
export const customTooltipFormatter = (
  value: any,
  name: string,
  props: any,
) => {
  if (typeof value === 'number') {
    if (
      name.toLowerCase().includes('percent') ||
      name.toLowerCase().includes('%')
    ) {
      return formatPercent(value)
    }
    return formatNumber(value)
  }
  return value
}
// Responsive chart configuration
export const CHART_RESPONSIVE = {
  small: {
    height: 250,
    margin: { top: 10, right: 20, bottom: 30, left: 40 },
  },
  medium: {
    height: 300,
    margin: { top: 20, right: 30, bottom: 40, left: 50 },
  },
  large: {
    height: 400,
    margin: { top: 20, right: 30, bottom: 50, left: 60 },
  },
}
